import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Music, Eye, Upload, Image, X, ShoppingBag, Calendar, MapPin, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRE_OPTIONS = [
  'Rock', 'Indie', 'Alternative', 'Punk', 'Post-Punk', 'Shoegaze',
  'Metal', 'Garage Rock', 'Blues Rock', 'Grunge', 'Lo-Fi',
  'Synth-Punk', 'Industrial', 'Electronic', 'Darkwave', 'Noise Rock',
  'Psych Rock', 'Folk', 'Americana', 'Hardcore', 'Emo', 'Honky Tonk', 'Country'
];

const BandDashboard = () => {
  const { user, getAuthHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [bands, setBands] = useState([]); // All bands owned by user
  const [selectedBandId, setSelectedBandId] = useState(null);
  const [band, setBand] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTrack, setNewTrack] = useState({ title: '', embed_url: '', track_type: 'single' });
  const [addTrackOpen, setAddTrackOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    bio: '',
    genre_tags: [],
    location: '',
    website: '',
    upcoming_shows: [{ venue: '', location: '', date: '', time: '', cost: '' }],
    donate_link: '',
    logo_url: '',
    image_urls: [''],
    merch_items: [{ name: '', url: '', description: '' }]
  });

  // Fetch all bands owned by user
  useEffect(() => {
    const fetchBands = async () => {
      try {
        const res = await fetch(`${API}/my-bands`, {
          headers: getAuthHeaders(),
          
        });
        
        if (res.ok) {
          const data = await res.json();
          setBands(data || []);
          
          // Auto-select first band if available
          if (data && data.length > 0) {
            setSelectedBandId(data[0].band_id);
          }
        }
      } catch (error) {
        console.error('Error fetching bands:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBands();
  }, [getAuthHeaders]);

  // Fetch selected band details and tracks
  useEffect(() => {
    const fetchBandDetails = async () => {
      if (!selectedBandId) return;
      
      try {
        const res = await fetch(`${API}/band/${selectedBandId}/details`, {
          headers: getAuthHeaders(),
          
        });
        
        if (res.ok) {
          const data = await res.json();
          setBand(data);
          setFormData({
            name: data.name || '',
            email: data.email || '',
            bio: data.bio || '',
            genre_tags: data.genre_tags || [],
            location: data.location || '',
            website: data.website || '',
            upcoming_shows: data.upcoming_shows?.length > 0 ? data.upcoming_shows : [{ venue: '', location: '', date: '', time: '', cost: '' }],
            donate_link: data.donate_link || '',
            logo_url: data.logo_url || '',
            image_urls: data.image_urls?.length > 0 ? data.image_urls : [''],
            merch_items: data.merch_items?.length > 0 ? data.merch_items : [{ name: '', url: '', description: '' }]
          });
          
          // Fetch tracks
          const tracksRes = await fetch(`${API}/bands/${selectedBandId}/tracks`, {
            headers: getAuthHeaders(),
            
          });
          if (tracksRes.ok) {
            setTracks(await tracksRes.json());
          }
        }
      } catch (error) {
        console.error('Error fetching band details:', error);
      }
    };
    
    fetchBandDetails();
  }, [selectedBandId, getAuthHeaders]);

  const toggleGenre = (genre) => {
    setFormData(prev => ({
      ...prev,
      genre_tags: prev.genre_tags.includes(genre)
        ? prev.genre_tags.filter(g => g !== genre)
        : [...prev.genre_tags, genre]
    }));
  };

  // Image handlers
  const addImage = () => {
    if (formData.image_urls.length < 5) {
      setFormData(prev => ({
        ...prev,
        image_urls: [...prev.image_urls, '']
      }));
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index)
    }));
  };

  const updateImage = (index, value) => {
    setFormData(prev => ({
      ...prev,
      image_urls: prev.image_urls.map((url, i) => i === index ? value : url)
    }));
  };

  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e, type, index = null) => {
    const file = e.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size, 'bytes');

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }

    // 50MB max
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Image must be less than 50MB');
      return;
    }

    setUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      // Get auth token from localStorage directly as backup
      const authHeaders = getAuthHeaders();
      const storedToken = localStorage.getItem('token');
      const headers = {};
      
      // Use auth headers or fallback to stored token
      if (authHeaders && authHeaders['Authorization']) {
        headers['Authorization'] = authHeaders['Authorization'];
      } else if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const uploadUrl = `${API}/upload/image`;
      console.log('Uploading to:', uploadUrl);
      console.log('Auth token present:', !!headers['Authorization']);
      
      if (!headers['Authorization']) {
        toast.error('Please log in again to upload images');
        setUploading(false);
        return;
      }
      
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: headers,
        body: uploadFormData
      });

      console.log('Upload response:', res.status, res.statusText);

      if (res.ok) {
        const data = await res.json();
        console.log('Upload success:', data);
        if (type === 'logo') {
          setFormData(prev => ({ ...prev, logo_url: data.url }));
        } else if (type === 'image' && index !== null) {
          updateImage(index, data.url);
        }
        toast.success('Image uploaded!');
      } else {
        // Clone the response to read it safely (avoids "body stream already read" error)
        const resClone = res.clone();
        let errorMsg = `Upload failed (${res.status})`;
        try {
          const errorData = await resClone.json();
          errorMsg = errorData.detail || errorMsg;
        } catch {
          try {
            const errorText = await res.text();
            errorMsg = errorText || errorMsg;
          } catch {
            // Use default error message
          }
        }
        console.error('Upload error:', res.status, errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Upload exception:', error.name, error.message);
      console.error('Full error:', error);
      toast.error(`Upload failed: ${error.message || 'Network error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteUploadedImage = async (url, type, index = null) => {
    const filename = url.split('/').pop();
    
    try {
      await fetch(`${API}/upload/image/${filename}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
    } catch (error) {
      // Continue anyway
    }
    
    if (type === 'logo') {
      setFormData(prev => ({ ...prev, logo_url: '' }));
    } else if (type === 'image' && index !== null) {
      removeImage(index);
    }
    toast.success('Image removed');
  };

  // Merch handlers
  const addMerchItem = () => {
    setFormData(prev => ({
      ...prev,
      merch_items: [...prev.merch_items, { name: '', url: '', description: '' }]
    }));
  };

  const removeMerchItem = (index) => {
    setFormData(prev => ({
      ...prev,
      merch_items: prev.merch_items.filter((_, i) => i !== index)
    }));
  };

  const updateMerchItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      merch_items: prev.merch_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Clean up empty entries
      const cleanedData = {
        ...formData,
        image_urls: formData.image_urls.filter(url => url.trim()),
        merch_items: formData.merch_items.filter(item => item.url.trim()),
        upcoming_shows: formData.upcoming_shows.filter(show => show.venue.trim() || show.date.trim())
      };
      
      const res = await fetch(`${API}/bands/${band.band_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(cleanedData),
        
      });
      
      if (res.ok) {
        const updated = await res.json();
        setBand(updated);
        toast.success('Band profile updated!');
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTrack = async () => {
    if (!newTrack.title.trim() || !newTrack.embed_url.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      const res = await fetch(`${API}/bands/${band.band_id}/tracks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(newTrack),
        
      });
      
      if (res.ok) {
        const track = await res.json();
        setTracks(prev => [track, ...prev]);
        setNewTrack({ title: '', embed_url: '', track_type: 'single' });
        setAddTrackOpen(false);
        toast.success('Track added!');
      }
    } catch (error) {
      toast.error('Failed to add track');
    }
  };

  const handleDeleteTrack = async (trackId) => {
    if (!confirm('Delete this track?')) return;
    
    try {
      const res = await fetch(`${API}/tracks/${trackId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setTracks(prev => prev.filter(t => t.track_id !== trackId));
        toast.success('Track deleted');
      }
    } catch (error) {
      toast.error('Failed to delete track');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-accent text-3xl">LOADING...</div>
      </div>
    );
  }

  if (bands.length === 0) {
    return (
      <div className="min-h-screen py-12" data-testid="no-band">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Music className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
          <h1 className="font-accent text-4xl text-foreground mb-4">NO BAND YET</h1>
          <p className="font-mono text-muted-foreground mb-8">
            You don't have an approved band profile yet. Apply to become a featured band!
          </p>
          <Button 
            onClick={() => navigate('/apply')}
            className="border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase"
          >
            Apply Now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="band-dashboard">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-accent text-4xl text-foreground">BAND DASHBOARD</h1>
            <p className="font-mono text-sm text-muted-foreground mt-1">
              Manage your band profile{bands.length > 1 ? 's' : ''}
            </p>
          </div>
          {band && (
            <Link to={`/band/${band.band_id}`}>
              <Button variant="outline" className="font-mono uppercase">
                <Eye className="w-4 h-4 mr-2" />
                View Public Profile
              </Button>
            </Link>
          )}
        </div>

        {/* Band Selector - only show if user has multiple bands */}
        {bands.length > 1 && (
          <div className="border-2 border-primary/30 bg-primary/5 p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Music className="w-4 h-4 text-primary" />
              <Label className="font-mono text-xs uppercase text-primary">Select Band to Edit</Label>
            </div>
            <div className="flex flex-wrap gap-2">
              {bands.map((b) => (
                <Button
                  key={b.band_id}
                  variant={selectedBandId === b.band_id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedBandId(b.band_id)}
                  className={`font-mono text-sm ${
                    selectedBandId === b.band_id 
                      ? 'bg-primary text-black' 
                      : 'hover:border-primary'
                  }`}
                  data-testid={`select-band-${b.band_id}`}
                >
                  {b.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {!band ? (
          <div className="text-center py-12 border-2 border-dashed border-border">
            <p className="font-mono text-muted-foreground">Select a band to edit</p>
          </div>
        ) : (
          <>
            {/* Edit Profile */}
            <div className="border-2 border-border bg-card p-6 mb-8 space-y-6">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <h2 className="font-sans text-xl font-bold">Profile Information</h2>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-primary text-black font-mono uppercase"
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="font-mono text-sm uppercase">Band Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-input border-2 border-border font-mono"
                  data-testid="edit-band-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="font-mono text-sm uppercase">Contact Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-input border-2 border-border font-mono"
                  data-testid="edit-band-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="font-mono text-sm uppercase">Bio</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                className="bg-input border-2 border-border font-mono min-h-[100px]"
                data-testid="edit-band-bio"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="font-mono text-sm uppercase">Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="bg-input border-2 border-border font-mono"
                data-testid="edit-band-location"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="font-mono text-sm uppercase">Genres</Label>
              <div className="flex flex-wrap gap-2">
                {GENRE_OPTIONS.map((genre) => (
                  <Button
                    key={genre}
                    type="button"
                    variant={formData.genre_tags.includes(genre) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleGenre(genre)}
                    className={`font-mono text-xs uppercase ${
                      formData.genre_tags.includes(genre) 
                        ? 'bg-primary text-black border-primary' 
                        : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                    }`}
                  >
                    {genre}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Logo & Images */}
        <div className="border-2 border-border bg-card p-6 mb-8 space-y-6">
          <h2 className="font-sans text-xl font-bold border-b border-border pb-4">
            Logo & Images
          </h2>
          
          <div className="space-y-4">
            <Label className="font-mono text-sm uppercase">Logo</Label>
            <div className="flex flex-col gap-4">
              {formData.logo_url ? (
                <div className="flex items-center gap-4">
                  <img src={formData.logo_url} alt="Logo preview" className="w-24 h-24 object-cover border-2 border-primary" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteUploadedImage(formData.logo_url, 'logo')}
                    className="text-destructive hover:text-destructive/80 font-mono text-xs"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="flex gap-4 items-center">
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-border hover:border-primary px-4 py-3 flex items-center gap-2 transition-colors">
                      <Upload className="w-5 h-5 text-muted-foreground" />
                      <span className="font-mono text-sm text-muted-foreground">
                        {uploading ? 'Uploading...' : 'Upload Logo'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={(e) => handleImageUpload(e, 'logo')}
                      className="hidden"
                      disabled={uploading}
                      data-testid="logo-upload-input"
                    />
                  </label>
                  <span className="font-mono text-xs text-muted-foreground">or</span>
                  <Input
                    value={formData.logo_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                    placeholder="Paste URL..."
                    className="bg-input border border-border font-mono flex-1"
                    data-testid="edit-logo-url"
                  />
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="font-mono text-sm uppercase">Band Images (up to 5)</Label>
              {formData.image_urls.filter(u => u).length < 5 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addImage}
                  className="font-mono text-xs uppercase"
                  data-testid="add-image-btn"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Image
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {formData.image_urls.map((url, index) => (
                <div key={index} className="space-y-2">
                  {url ? (
                    <div className="relative group">
                      <img src={url} alt={`Image ${index + 1}`} className="w-full aspect-square object-cover border-2 border-border" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        onClick={() => handleDeleteUploadedImage(url, 'image', index)}
                        className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <div className="w-full aspect-square border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center gap-2 transition-colors">
                        <Upload className="w-6 h-6 text-muted-foreground" />
                        <span className="font-mono text-xs text-muted-foreground">
                          {uploading ? 'Uploading...' : 'Upload'}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={(e) => handleImageUpload(e, 'image', index)}
                        className="hidden"
                        disabled={uploading}
                        data-testid={`image-upload-${index}`}
                      />
                    </label>
                  )}
                  {!url && (
                    <Input
                      value={url}
                      onChange={(e) => updateImage(index, e.target.value)}
                      placeholder="Or paste URL"
                      className="bg-input border border-border font-mono text-xs"
                      data-testid={`edit-image-url-${index}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* External Links */}
        <div className="border-2 border-border bg-card p-6 mb-8 space-y-6">
          <h2 className="font-sans text-xl font-bold border-b border-border pb-4">
            External Links
          </h2>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="font-mono text-sm uppercase">Website</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://..."
                className="bg-input border-2 border-border font-mono"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="font-mono text-sm uppercase">Donate / Support</Label>
              <Input
                value={formData.donate_link}
                onChange={(e) => setFormData(prev => ({ ...prev, donate_link: e.target.value }))}
                placeholder="https://..."
                className="bg-input border-2 border-border font-mono"
              />
            </div>
          </div>
        </div>

        {/* Upcoming Shows */}
        <div className="border-2 border-border bg-card p-6 mb-8 space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <h2 className="font-sans text-xl font-bold flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Upcoming Shows
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                upcoming_shows: [...prev.upcoming_shows, { venue: '', location: '', date: '', time: '', cost: '' }] 
              }))}
              className="font-mono text-xs uppercase"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Show
            </Button>
          </div>
          
          <p className="font-mono text-xs text-muted-foreground">
            List your upcoming shows so fans can find you live
          </p>

          {formData.upcoming_shows.map((show, index) => (
            <div key={index} className="border border-border p-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="font-mono text-xs text-primary uppercase">Show {index + 1}</span>
                {formData.upcoming_shows.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      upcoming_shows: prev.upcoming_shows.filter((_, i) => i !== index)
                    }))}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Venue Name</Label>
                  <Input
                    value={show.venue}
                    onChange={(e) => {
                      const updated = [...formData.upcoming_shows];
                      updated[index].venue = e.target.value;
                      setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                    }}
                    placeholder="The Collective"
                    className="bg-input border border-border font-mono"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Location
                  </Label>
                  <Input
                    value={show.location}
                    onChange={(e) => {
                      const updated = [...formData.upcoming_shows];
                      updated[index].location = e.target.value;
                      setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                    }}
                    placeholder="Sedona, AZ"
                    className="bg-input border border-border font-mono"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Date</Label>
                  <Input
                    type="date"
                    value={show.date}
                    onChange={(e) => {
                      const updated = [...formData.upcoming_shows];
                      updated[index].date = e.target.value;
                      setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                    }}
                    className="bg-input border border-border font-mono"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Time</Label>
                  <Input
                    value={show.time}
                    onChange={(e) => {
                      const updated = [...formData.upcoming_shows];
                      updated[index].time = e.target.value;
                      setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                    }}
                    placeholder="8:00 PM"
                    className="bg-input border border-border font-mono"
                  />
                </div>
                
                <div className="space-y-2 sm:col-span-2">
                  <Label className="font-mono text-xs uppercase flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Cost
                  </Label>
                  <Input
                    value={show.cost}
                    onChange={(e) => {
                      const updated = [...formData.upcoming_shows];
                      updated[index].cost = e.target.value;
                      setFormData(prev => ({ ...prev, upcoming_shows: updated }));
                    }}
                    placeholder="$15, Free, $10-20"
                    className="bg-input border border-border font-mono"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Merch Items */}
        <div className="border-2 border-border bg-card p-6 mb-8 space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <h2 className="font-sans text-xl font-bold">Merch Links</h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addMerchItem}
              className="font-mono text-xs uppercase"
              data-testid="add-merch-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Merch
            </Button>
          </div>
          
          {formData.merch_items.map((item, index) => (
            <div key={index} className="border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-muted-foreground uppercase flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4" />
                  Merch Item {index + 1}
                </span>
                {formData.merch_items.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeMerchItem(index)}
                    className="text-destructive hover:text-destructive/80 h-6 w-6"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Item Name</Label>
                  <Input
                    value={item.name}
                    onChange={(e) => updateMerchItem(index, 'name', e.target.value)}
                    placeholder="T-Shirt, Vinyl, etc."
                    className="bg-input border border-border font-mono"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Link</Label>
                  <Input
                    value={item.url}
                    onChange={(e) => updateMerchItem(index, 'url', e.target.value)}
                    placeholder="https://..."
                    className="bg-input border border-border font-mono"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase">Description (optional)</Label>
                <Input
                  value={item.description || ''}
                  onChange={(e) => updateMerchItem(index, 'description', e.target.value)}
                  placeholder="Brief description..."
                  className="bg-input border border-border font-mono"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Tracks Management */}
        <div className="border-2 border-border bg-card p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <h2 className="font-sans text-xl font-bold">Tracks</h2>
            <Dialog open={addTrackOpen} onOpenChange={setAddTrackOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-black font-mono uppercase" data-testid="add-track-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Track
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-2 border-border">
                <DialogHeader>
                  <DialogTitle className="font-sans">Add New Track</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Title</Label>
                    <Input
                      value={newTrack.title}
                      onChange={(e) => setNewTrack(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Track title"
                      className="bg-input border border-border font-mono"
                      data-testid="new-track-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Embed URL</Label>
                    <Input
                      value={newTrack.embed_url}
                      onChange={(e) => setNewTrack(prev => ({ ...prev, embed_url: e.target.value }))}
                      placeholder="https://www.youtube.com/embed/... or Spotify embed URL"
                      className="bg-input border border-border font-mono"
                      data-testid="new-track-url"
                    />
                    <p className="font-mono text-xs text-muted-foreground">
                      Supports YouTube, Spotify, SoundCloud, Bandcamp embeds
                    </p>
                    <div className="bg-primary/10 border border-primary/30 p-2 rounded mt-2 space-y-1">
                      <p className="font-mono text-xs text-primary">
                        <strong>Tip for Spotify tracks:</strong> Click the three dots next to the song → Share → Copy link to Song.
                      </p>
                      <p className="font-mono text-xs text-primary">
                        <strong>Tip for full discography:</strong> Go to your Spotify artist profile → click the three dots (next to "Following") → Share → Copy Artist link. Use the "Album" dropdown option for this, the other options won't work properly.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Type</Label>
                    <select
                      value={newTrack.track_type}
                      onChange={(e) => setNewTrack(prev => ({ ...prev, track_type: e.target.value }))}
                      className="w-full bg-input border border-border font-mono text-sm p-2"
                      data-testid="new-track-type"
                    >
                      <option value="single">Single</option>
                      <option value="ep">EP</option>
                      <option value="album">Album</option>
                      <option value="track">Track</option>
                    </select>
                  </div>
                  <Button 
                    onClick={handleAddTrack}
                    className="w-full bg-primary text-black font-mono uppercase"
                    data-testid="confirm-add-track"
                  >
                    Add Track
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {tracks.length === 0 ? (
            <p className="font-mono text-muted-foreground text-center py-8">
              No tracks yet. Add your first track!
            </p>
          ) : (
            <div className="space-y-3">
              {tracks.map((track) => (
                <div 
                  key={track.track_id}
                  className="border border-border p-4 flex items-center justify-between"
                  data-testid={`dashboard-track-${track.track_id}`}
                >
                  <div>
                    <h3 className="font-sans font-bold text-foreground">{track.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="font-mono text-[10px] uppercase">
                        {track.track_type}
                      </Badge>
                      <span className="font-mono text-xs text-muted-foreground">
                        ♥ {track.heart_count}
                      </span>
                    </div>
                  </div>
                  <Button 
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteTrack(track.track_id)}
                    className="text-destructive hover:text-destructive/80"
                    data-testid={`delete-track-${track.track_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BandDashboard;
